import { inject, Injectable } from '@angular/core';
import {
  first,
  map,
  mergeMap,
  Observable,
  ReplaySubject,
  switchMap,
  tap
} from 'rxjs';
import { ObrasciUnosaStore } from './obrasci-unosa-store';
import { View, InputControlData, Attribute } from '@ntap/core';

export type ObrazacUnosaDTO = {
  id: number;
  name: string;
  title: string;
  grupaObrasca: string;
};

export type ObrazacUnosaDropdownOption = {
  label: string;
  value?: number;
  items?: Array<ObrazacUnosaDropdownOption>;
};

export const DEFAULT_ITEM_ENTRY_TEMPLATE_VIEW_NAME =
  'entry_template_sazeti_opis_item';
export const DEFAULT_LIST_ENTRY_TEMPLATE_VIEW_NAME =
  'entry_template_sazeti_opis';

@Injectable()
export class ObrazacUnosaService {
  private entryTemplatesStore = inject(ObrasciUnosaStore);

  selectEntryTemplate$: Observable<ObrazacUnosaDTO>;

  entryTemplates$: Observable<ObrazacUnosaDTO[]>;

  entryTemplateDropdownOptions$: Observable<Array<ObrazacUnosaDropdownOption>>;

  private selectEntryTemplateSubject = new ReplaySubject<ObrazacUnosaDTO>(1);

  constructor() {
    this.initEntryTemplatesObservable();
    this.entryTemplates$ = this.entryTemplatesStore.entryTemplates$;
    this.entryTemplateDropdownOptions$ =
      this.entryTemplatesStore.entryTemplateDropdownOptions$;
  }

  initEntryTemplatesObservable() {
    this.selectEntryTemplate$ = this.selectEntryTemplateSubject.asObservable();
  }

  refreshEntryTemplateOptions() {
    return this.entryTemplatesStore.refreshEntryTemplateOptions();
  }

  refetchEntryTemplateOptionsAndSelectEntryTemplate(id: number) {
    return this.refreshEntryTemplateOptions().pipe(
      switchMap(() => {
        return this.selectEntryTemplateById(id);
      })
    );
  }

  selectDefaultEntryTemplate() {
    this.entryTemplates$
      .pipe(
        first(),
        tap((entryTemplates) => {
          entryTemplates
            .filter((e) => e.name === DEFAULT_ITEM_ENTRY_TEMPLATE_VIEW_NAME)
            .forEach((e) => {
              this.selectEntryTemplateSubject.next(e);
            });
        })
      )
      .subscribe();
  }

  selectEntryTemplate(entryTemplate: ObrazacUnosaDTO) {
    this.selectEntryTemplateSubject.next(entryTemplate);
    return entryTemplate;
  }

  selectEntryTemplateById(id: number) {
    return this.resolveEntryTemplate(id).pipe(
      tap((entryTemplate) => {
        if (entryTemplate) {
          this.selectEntryTemplate(entryTemplate);
        }
      })
    );
  }

  refetchEntryTemplateOptionsAndSelectFirstTemplate() {
    return this.refreshEntryTemplateOptions().pipe(
      mergeMap((entryTemplates) =>
        this.selectEntryTemplateById(entryTemplates[0].id)
      )
    );
  }

  private resolveEntryTemplate(id: number) {
    return this.entryTemplatesStore.entryTemplates$.pipe(
      first(),
      map((entryTemplates) => {
        const selectedObrazacUnosa = entryTemplates.find((t) => {
          return t.id == id;
        });
        if (selectedObrazacUnosa) {
          return this.selectEntryTemplate(selectedObrazacUnosa);
        }
        return entryTemplates[0]; // default na prvu
      })
    );
  }

  public getItemViewAttrsOrderedByListViewAttrs(
    entryTemplateListView: View,
    entryTemplateItemViewInputs: InputControlData[]
  ): InputControlData[] {
    return ([] as InputControlData[]).concat(
      ...entryTemplateListView.attributes.map((listViewAttr: Attribute) => {
        const filtered = entryTemplateItemViewInputs.filter((itemViewAttr) => {
          return (
            itemViewAttr.attributeMetadata.name === listViewAttr.name ||
            // special case -> labela je u listi, a u traci je vrijeme_nastanka s inputom
            (listViewAttr.name ===
              'en~jedinica_gradiva~vrijeme_nastanka_labela' &&
              itemViewAttr.attributeMetadata.name ===
                'en~jedinica_gradiva~vrijeme_nastanka') ||
            (listViewAttr.name === 'en~jedinica_gradiva~mjesto_labela' &&
              itemViewAttr.attributeMetadata.name ===
                'en~jedinica_gradiva~mjesto')
          );
        });
        return filtered;
      })
    );
  }
}
